/**
 * Hero Brand Utilities
 * Helper functions for managing Hero brand implementation
 */

import { getBusinessTypeTag } from './businessType';

/**
 * Check if Hero brand is enabled
 * Determines if the Hero brand should be applied based on hostname or user business type.
 * @returns {boolean} True if domain or business type tag includes 'herotolk'
 */
export const isHeroBrandEnabled = () => {
  if (window?.location?.hostname.includes('herotolk')) {
    return true;
  }

  return (getBusinessTypeTag() || '').includes('herotolk');
};

// Apply Hero brand class to body
export const applyHeroBrandStyles = () => {
  if (isHeroBrandEnabled()) {
    document.body.classList.add('hero-brand');
  } else {
    document.body.classList.remove('hero-brand');
  }
};

// Get Hero brand colors
export const getHeroBrandColors = () => {
  return {
    purple: '#8B1C93',
    beige: '#E2DBC8',
    darkBrown: '#531530',
    blue: '#0093B1',
    green: '#1DAF8E',
    coral: '#F08372',
    beigeBackground: 'rgba(226, 219, 200, 0.2)'
  };
};

// Get Hero brand fonts
export const getHeroBrandFonts = () => {
  return {
    primary: 'Arial, Helvetica, sans-serif',
    special: 'Bradley Hand ITC, cursive',
    fallback: 'Arial, sans-serif'
  };
};

// Get Hero brand taglines
export const getHeroTaglines = () => {
  return {
    norwegian: 'Gjør en ny framtid mulig',
    english: 'Enabling a new future',
    german: 'Eine neue Zukunft möglich machen'
  };
};

// Initialize Hero brand on app load
export const initializeHeroBrand = () => {
  // Apply styles
  applyHeroBrandStyles();

  // Set CSS custom properties if Hero brand is enabled
  if (isHeroBrandEnabled()) {
    const colors = getHeroBrandColors();
    const fonts = getHeroBrandFonts();

    const root = document.documentElement;

    // Set color variables
    root.style.setProperty('--hero-purple', colors.purple);
    root.style.setProperty('--hero-beige', colors.beige);
    root.style.setProperty('--hero-dark-brown', colors.darkBrown);
    root.style.setProperty('--hero-blue', colors.blue);
    root.style.setProperty('--hero-green', colors.green);
    root.style.setProperty('--hero-coral', colors.coral);
    root.style.setProperty('--hero-beige-background', colors.beigeBackground);

    // Set font variables
    root.style.setProperty('--hero-font-family', fonts.primary);
    root.style.setProperty('--hero-font-special', fonts.special);

    console.log('Hero brand initialized');
  }
};

// Component wrapper for Hero brand conditional rendering
export const withHeroBrand = (HeroComponent, FallbackComponent = null) => {
  return (props) => {
    if (isHeroBrandEnabled()) {
      return HeroComponent ? <HeroComponent {...props} /> : null;
    }
    return FallbackComponent ? <FallbackComponent {...props} /> : null;
  };
};

// Hero brand configuration object
export const heroBrandConfig = {
  enabled: isHeroBrandEnabled(),
  colors: getHeroBrandColors(),
  fonts: getHeroBrandFonts(),
  taglines: getHeroTaglines(),

  // Service area icons
  serviceAreas: [
    { name: 'mottak', label: 'Mottak' },
    { name: 'tolk', label: 'Tolk' },
    { name: 'kurs', label: 'Kurs' }
  ],

  // Company values
  values: [
    { name: 'likeverd', label: 'Likeverd' },
    { name: 'profesjonalitet', label: 'Profesjonalitet' },
    { name: 'apenhet', label: 'Åpenhet' }
  ],

  // Dialog bubble variants
  dialogBubbleVariants: ['normal', 'high', 'long'],

  // Graphic pattern variants (1-5 as per manual)
  graphicPatternVariants: [1, 2, 3, 4, 5]
};

export default heroBrandConfig;